<template>
  <router-link class="list-item"
      :to="{ id: 'detail', params: { providerId: provider.providerId } }">
    <div class="provider-card">
      <div class="provider-head">
        <div class="provider-background-image" :style="coverImage"></div>
        <div class="background-image-items">
          <div v-if="hasLimitedOffer" class="limited-offer-tag">
            {{ $t('discount.limitedOffer.tag') }}
          </div>
          <provider-list-item-logo :source="provider.logo" />
        </div>
      </div>
      <div class="provider-info">
        <div class="provider-name">
          <L10n :item="provider" name="name" />
        </div>
        <L10n class="provider-benefits-summary" :item="provider.benefits"
          l10nName="summary" name="benefitSummary" />
      </div>
      <div class="benefit-info-wraper">
        <div class="category-country-wrapper">
          <provider-categories :benefitCategories="benefitCategories" />
          <provider-countries :discounts="provider.benefits" />
        </div>
      </div>
    </div>
  </router-link>
</template>

<style scoped>
  .list-item {
    flex: 0 1 33%;
    text-decoration: none;
  }

  .provider-card {
    display: flex;
    flex-direction: column;
    height: 320px;
    max-width: 450px;
    margin-right: 16px;
    margin-bottom: 16px;
    border-radius: 8px;
    background-color: #F7F9FA;
    border: 1px solid #EAEDEF;
  }

  @media screen and (max-width: 1280px) {
    .list-item {
      flex: 0 1 40%;
    }
    .provider-card {
      min-width: 400px;
    }
  }

  @media screen and (max-width: 860px) {
    .list-item {
      flex: 0 1 100%;
      max-width: 600px;
    }

    .provider-card {
      max-width: 600px;
      margin-right: 0;
    }
  }

  @media screen and (max-width: 450px) {
    .provider-card {
      min-width: fit-content;
    }
  }

  .provider-card:hover .provider-name {
    color: var(--primary-color);
  }

  .provider-head {
    min-height: 164px;
    padding: 0;
  }

  .provider-info {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0 16px 16px 16px;
  }

  .provider-name {
    color: #2f3336;
    font-weight: 700;
    font-size: 18px;
    margin-bottom: 16px;
  }

  .provider-benefits-summary {
    color: #646a70;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    font-size: 14px;
    overflow: hidden;
  }

  .limited-offer-tag {
    position: absolute;
    top: -120px;
    left: 0px;
    height: 16px;
    background-color: #ed9e25;
    color: white;
    font-weight: 900;
    border-radius: 2px;
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    padding: 8px;
  }

  .background-image-items {
    display: inline-flex;
    width: 100%;
    height: 100%;
    position: relative;
  }

  .provider-background-image {
    position: relative;
    height: 150px;
    width: 100%;
    overflow: hidden;
    border-top-right-radius: 8px;
    border-top-left-radius: 8px;
  }

  .provider-background-image::after {
    content: "";
    display: block;
    position: absolute;
    width: 105%;
    height: 0;
    background: #F7F9FA;
    bottom: 0;
    left: 0;
    transform: translateY(100%) skewY(-4deg);
    transform-origin: bottom left;
    /* sin(skewY angle) = x
    sin(4) = 0.0697
    padding-bottom = x * 100 */
    padding-bottom: 7%;
  }

  .benefit-info-wraper {
    margin-left: 16px;
    margin-bottom: 16px;
  }

  .category-country-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    height: 36px;
    padding-right: 16px;
  }
</style>

<script>
import RouterLink from '@/router/RouterLink.vue';
import { ProviderType } from '@/types';
import ProviderListItemLogo from './ProviderListItemLogo.vue';
import L10n from '../L10n.vue';
import ProviderCountries from '../country/ProviderCountries.vue';
import ProviderCategories from './ProviderCategories.vue';

export default {
  name: 'provider-list-item',
  props: {
    provider: {
      type: ProviderType,
      required: true,
    },
  },
  computed: {
    coverImage() {
      const NORMAL_SIZE_INDEX = 0;
      const providerBenefits = this.provider.benefits;
      const benefitsMedia = providerBenefits.map((benefit) => benefit.media);
      let coverImage = null;
      // Try to find cover image from the benefits.
      for (let i = 0; i < benefitsMedia.length; i += 1) {
        const benefitMedia = benefitsMedia[i];
        if (benefitMedia && !coverImage) {
          for (let j = 0; j < benefitMedia.length; j += 1) {
            const media = benefitMedia[j];
            if (media.type === 'IMAGE' && media.images[NORMAL_SIZE_INDEX].width > 400
                && media.images[NORMAL_SIZE_INDEX].height > 200) {
              coverImage = `background: url("${media.images[NORMAL_SIZE_INDEX].url}"); background-size: cover;`;
              return coverImage;
            }
          }
        }
      }
      if (coverImage) {
        return coverImage;
      }
      // If no image was found in the benefits take coverImage from category.
      const providerCategoryId = providerBenefits[0].primaryCategory.categoryId;
      // eslint-disable-next-line max-len
      const providerCategory = this.$store.getters.getAllCategories.filter((category) => category.categoryId === providerCategoryId)[0];
      return `background: url("${providerCategory.coverImageUrl}"); background-size: cover;`;
    },
    benefitCategories() {
      const allCategories = this.$store.getters.getAllCategories;
      const categories = [];
      this.provider.benefits.forEach((benefit) => {
        const providerCategoryId = benefit.primaryCategory.categoryId;
        if (!categories.some((item) => item.categoryId === providerCategoryId)) {
          // eslint-disable-next-line max-len
          categories.push(allCategories.filter((category) => category.categoryId === providerCategoryId)[0]);
        }
        if (benefit.secondaryCategories) {
          benefit.secondaryCategories.forEach((secondaryCategory) => {
            if (!categories.some((item) => item.categoryId === secondaryCategory.categoryId)) {
              // eslint-disable-next-line max-len
              categories.push(allCategories.filter((category) => category.categoryId === secondaryCategory.categoryId)[0]);
            }
          });
        }
      });
      return categories;
    },
    hasLimitedOffer() {
      const limited = this.provider.benefits.filter((benefit) => benefit.limitedOffer === true);
      return limited.length !== 0;
    },
  },
  components: {
    RouterLink,
    ProviderListItemLogo,
    L10n,
    ProviderCountries,
    ProviderCategories,
  },
};
</script>
